import { Button, IconFrame, IconFrameButton } from '@cleanlab/design-system/components'
import { IconInfo, IconX } from '@cleanlab/design-system/icons'
import { cn } from '@cleanlab/design-system/utils'
import { type Query } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useOrganization } from '@/api/organizations'
import { type OrganizationSchemaPublic } from '@/client/types.gen'
import { ERROR_DOM_ID } from '@/global/consts'
import { CODEX_NOTIFICATION_DESCRIPTIONS, CODEX_NOTIFICATION_TITLES } from '@/global/utils'
import { useUser } from '@/providers/account/UserContextProvider'

const NOTIFICATION_DISMISSED_KEY = 'codex-notification-dismissed'

export const TopLevelCodexNotification: React.FC = () => {
  const { selectedOrganizationId } = useUser()
  const { data: organization } = useOrganization(selectedOrganizationId, {
    enabled: !!selectedOrganizationId,
    // Refetch every 30 seconds if payment status exists and isn't NULL
    refetchInterval: (query: Query<OrganizationSchemaPublic>) => {
      const data = query.state.data
      return data?.payment_status && data.payment_status !== 'NULL' ? 30000 : false
    },
  })
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(true)
  const targetDom = document.getElementById(ERROR_DOM_ID)

  // Only show notification if payment status exists, and if there's no card details
  const shouldShowNotification =
    organization?.payment_status && organization.payment_status !== 'NULL'
  const variant = organization?.payment_status === 'FIRST_OVERAGE_LENIENT' ? 'info' : 'error'
  const isInfo = variant === 'info'
  const title = CODEX_NOTIFICATION_TITLES[variant]
  const description = CODEX_NOTIFICATION_DESCRIPTIONS[variant]

  useEffect(() => {
    const isDismissed = localStorage.getItem(NOTIFICATION_DISMISSED_KEY) === 'true'
    setIsVisible(!isDismissed)
  }, [])

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem(NOTIFICATION_DISMISSED_KEY, 'true')
  }

  const handleAddPayment = () => {
    router.push('/account?tab=billing&openStripeModal=true')
  }

  // Return null if we shouldn't show the notification
  if (!shouldShowNotification || !isVisible || !targetDom) return null

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={cn(
            'mx-8 mt-8 flex items-center justify-between overflow-hidden rounded-2 border bg-surface-1 p-5',
            isInfo ? 'border-blue-100 bg-blue-50' : 'border-red-100 bg-red-50'
          )}
        >
          <div className="flex max-w-[70%] items-center gap-8">
            <IconFrame
              icon={<IconInfo className={isInfo ? 'text-blue-500' : 'text-red-500'} />}
              variant="iconOnly"
              size="large"
            />
            <div className="flex flex-col">
              <p
                className={cn('type-body-200-semibold', isInfo ? 'text-blue-900' : 'text-red-900')}
              >
                {title}
              </p>
              <p className={cn('type-body-100', isInfo ? 'text-blue-900' : 'text-red-900')}>
                {description}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-1">
            <Button
              variant={isInfo ? 'primary' : 'critical'}
              size="small"
              onClick={handleAddPayment}
            >
              Add payment information
            </Button>
            {isInfo && (
              <IconFrameButton
                icon={<IconX />}
                variant="iconOnly"
                size="small"
                aria-label="Close"
                onClick={handleDismiss}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    targetDom as HTMLElement
  )
}
